<template>
  <div class="parent-wrapper">
    <p v-if="!showReviewSection" class="black--text mt-3 font-weight-bold">{{ $t("taxForm.w-9.formCaption") }}</p>
    <validation-observer slim ref="observer" v-slot="{ invalid }">
      <v-form v-if="!showReviewSection" autocomplete="off">
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.nameCountry") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="w8Form.legalName"
              :placeholder="$t('taxForm.captions.enterFullName')"
              :label="$t('taxForm.captions.enterFullName')"
              :hint="namespacingHint"
              id="fullname"
              rules="required|name"
              :multiLang="false"
              persistent-hint
              :maxLength="255"
              @input="hasSpacingIrregularities"
              @blur="saveIfValid"
            >
            </VTextFieldWithValidation>
          </v-col>
          <v-col cols="12" md="6">
            <VCountrySelectorWithValidation
              class="list-items-spacing"
              v-model="w8Form.countryId"
              attach
              rules="required"
              placeholder="countryCitizenship"
              label="countryCitizenship"
              autocomplete="country_citizenship"
              @change="saveIfValid"
            >
            </VCountrySelectorWithValidation>
          </v-col>
        </v-row>
        <v-divider class="my-5"></v-divider>
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.permanentAddress") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="w8Form.address"
              name="address"
              clearable
              placeholder="addressWithApt"
              label="addressWithApt"
              rules="required"
              :maxLength="255"
              @blur="saveIfValid"
            >
            </VTextFieldWithValidation>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              placeholder="city"
              name="city"
              clearable
              v-model.trim="w8Form.city"
              label="city"
              rules="required"
              :maxLength="100"
              @blur="saveIfValid"
            ></VTextFieldWithValidation>
          </v-col>
          <v-col cols="12" md="6">
            <v-country-states-selector-with-validation
              clearable
              v-model="w8Form.stateId"
              @state-selected="currentState"
              rules=""
              id="state"
              name="state"
              attach
              placeholder="selectYourState"
              :countryId="w8Form.countryId ? w8Form.countryId : 0"
              autocomplete="new-state"
              label="selectYourState"
              :disabled="!w8Form.countryId"
              @change="saveIfValid"
            />
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              clearable
              v-model.trim="w8Form.zipCode"
              placeholder="enterZipCode"
              name="zipcode"
              autocomplete="new-zip-code"
              label="enterZipCode"
              rules=""
              :maxLength="10"
              @blur="saveIfValid"
            >
            </VTextFieldWithValidation>
          </v-col>
        </v-row>
        <v-divider class="my-6"></v-divider>
        <v-row justify="center" class="mb-2">
          <v-col cols="12" md="7">
            <p class="black--text text-center mb-0">
              Provide either a U.S. Tax Identification Number (TIN) or a
              <br class="d-none d-md-block" />
              Foreign Tax Identification Number (FTIN).
            </p>
            <v-row justify="center" class="mt-1">
              <v-radio-group
                v-model="notRequiredSelection"
                row
                hide-details
                @change="handleNotRequired"
              >
                <v-radio :label="$t('fields.tinNotRequired')" value="tin" class="mr-6"/>
                <v-radio :label="$t('fields.ftinNotRequired')" value="ftin"/>
              </v-radio-group>
            </v-row>
          </v-col>
        </v-row>
        <v-row align="center" class="mb-2">
          <v-col cols="12" md="8">
            <h3 class="primary--text mb-0">{{ $t("taxForm.captions.identificationNumber") }}</h3>
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-if="hasSavedTin && w8Form.ssn && !editingTin"
              :value="maskedSsn"
              label="Social Security Number"
              readonly
              class="masked-tin-field"
              append-icon="$edit"
              @click:append="editTin"
              :disabled="tinFieldsDisabled"
            />
            <v-tooltip v-else bottom :disabled="!tinFieldsDisabled" content-class="tin-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <VTextFieldWithValidation
                    v-model="w8Form.ssn"
                    name="securityNumber"
                    rules="ssnFormat"
                    :disabled="tinFieldsDisabled"
                    @blur="saveIfValid"
                    label="securityNumber"
                    placeholder="securityNumber"
                    :append-icon="showSecurityNumber ? '$eye' : '$eyeSlash'"
                    :type="showSecurityNumber ? 'text' : 'password'"
                    @input="formatSsn"
                    @click:append="showSecurityNumber = !showSecurityNumber"
                    clearable
                    :maxLength="11"
                  />
                </div>
              </template>
              <span>To enable this field, select FTIN not required.</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center justify-center"
            ><span class="primary--text text-center">OR</span></v-col
          >
          <v-col cols="12" md="5">
            <v-text-field
              v-if="hasSavedTin && w8Form.itin && !editingTin"
              :value="maskedItin"
              label="ITIN"
              readonly
              class="masked-tin-field"
              append-icon="$edit"
              @click:append="editTin"
              :disabled="tinFieldsDisabled"
            />
            <v-tooltip v-else bottom :disabled="!tinFieldsDisabled" content-class="tin-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <VTextFieldWithValidation
                    v-model="w8Form.itin"
                    name="employerIdentification"
                    rules="itinFormat"
                    @blur="saveIfValid"
                    label="itin"
                    placeholder="itin"
                    hint="itinHint"
                    persistent-hint
                    clearable
                    :append-icon="showIdentificationNumber ? '$eye' : '$eyeSlash'"
                    :type="showIdentificationNumber ? 'text' : 'password'"
                    autocomplete="off"
                    inputmode="numeric"
                    @input="formatItin"
                    @click:append="showIdentificationNumber = !showIdentificationNumber"
                    :readonly="true"
                    @focus="removeReadOnly"
                    :maxLength="11"
                    :disabled="tinFieldsDisabled"
                  />
                </div>
              </template>
              <span>To enable this field, select FTIN not required.</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row align="center" class="mt-2 mb-2">
          <v-col cols="12" md="8">
            <h3 class="primary--text mb-0"> {{ $t("taxForm.captions.foreignTaxNumber") }}</h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-tooltip bottom :disabled="!ftinFieldDisabled" content-class="tin-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    v-model="w8Form.ftin"
                    name="ftinNumber"
                    :rules="ftinRules"
                    :label="$t('fields.ftin')"
                    :placeholder="$t('placeholders.ftin')"
                    persistent-hint
                    autocomplete="new-tin"
                    :success="!ftinFieldDisabled"
                    :append-icon="showFtNum ? '$eye' : '$eyeSlash'"
                    :type="showFtNum ? 'text' : 'password'"
                    @click:append="showFtNum = !showFtNum"
                    clearable
                    :disabled="ftinFieldDisabled"
                    :maxLength="25"
                    @blur="saveIfValid"
                  />
                </div>
              </template>
              <span>To enable this field, select TIN not required.</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-divider class="my-6"></v-divider>
        <v-row align="center" class="mb-2">
          <v-col cols="12" md="8">
            <h3 class="primary--text mb-0">{{ $t("wizard.review.dob") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VDateSelectorWithValidation
              clearable
              v-model="dateTimeStamp"
              name="dateSelector"
              rules="date"
              label="dob"
              minMax="min"
              placeholder="dob"
              @blur="saveIfValid"
            />
          </v-col>
        </v-row>
        <v-divider class="my-6"></v-divider>
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.paypalInfo") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="w8Form.paypalEmail"
              name="paypalEmail"
              :rules="paypalMailRules"
              label="Paypal Email"
              placeholder="Paypal Email"
              :readonly="true"
              @focus="removeReadOnly"
              :disabled="w8Form.doNotHavePaypal"
              :toggleErrorClass="true"
              persistent-hint
              :multiLang="false"
              :maxLength="255"
              @blur="saveIfValid"
            />
            <p v-if="!w8Form.doNotHavePaypal" class="mb-0 user-info-content black--text">
              {{ $t("fields.papalPaymentInfo") }}
            </p>
          </v-col>
          <v-col cols="12" md="6" class="d-flex" :class="{ 'justify-center': !isMobile }">
            <v-checkbox
              v-model="w8Form.doNotHavePaypal"
              color="primary"
              class="black--text black--text custom-label"
              :label="$t('fields.paypalCheck')"
              @change="saveIfValid"
            ></v-checkbox>
          </v-col>

          <v-col cols="12">
            <v-row class="test" v-if="w8Form.doNotHavePaypal">
              <v-col cols="12">
                <p class="black--text font-weight-bold">Please give us the following information:</p>
              </v-col>
              <v-col cols="12" md="6">
                <VTelephoneNumberWithValidation
                  v-model="w8Form.personalPhoneNumber"
                  :toggleErrorClass="true"
                  name="phoneNumber"
                  rules="required"
                  :hint="''"
                  label="personalNumber"
                  placeholder="personalNumber"
                  :maxLength="50"
                  @blur="saveIfValid"
                />
                <p class="mb-0 user-info-content black--text">{{ $t("hints.fields.telephone") }}</p>
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  v-model.trim="w8Form.bankName"
                  name="bankName"
                  rules="required"
                  label="bankName"
                  placeholder="bankName"
                  :maxLength="100"
                  @blur="saveIfValid"
                />
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  v-model.trim="w8Form.swiftCode"
                  name="bankCode"
                  rules="required|min:8"
                  label="bankswiftCode"
                  placeholder="bankswiftCode"
                  :maxLength="11"
                  @blur="saveIfValid"
                />
              </v-col>
              <v-col cols="12" md="6">
                <VCountrySelectorWithValidation
                  class="list-items-spacing"
                  v-model="w8Form.bankCountry"
                  :toggleErrorClass="true"
                  attach
                  rules="required"
                  placeholder="bankCountry"
                  label="bankCountry"
                  autocomplete="bank-country"
                  @change="saveIfValid"
                >
                </VCountrySelectorWithValidation>
                <p class="mb-0 user-info-content primary--text">{{ $t("fields.bankCountryInfo") }}</p>
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  v-model.trim="w8Form.bankAddress1"
                  name="addressLine1"
                  clearable
                  placeholder="addressLine1"
                  label="addressLine1"
                  rules="required"
                  :maxLength="255"
                  @blur="saveIfValid"
                >
                </VTextFieldWithValidation>
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  v-model.trim="w8Form.bankAddress2"
                  name="addressLine2"
                  clearable
                  placeholder="addressLine2"
                  label="addressLine2"
                  rules=""
                  :maxLength="255"
                  @blur="saveIfValid"
                >
                </VTextFieldWithValidation>
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  placeholder="bankCity"
                  name="bankCity"
                  clearable
                  v-model.trim="w8Form.bankCity"
                  label="bankCity"
                  rules="required"
                  :maxLength="100"
                  @blur="saveIfValid"
                >
                </VTextFieldWithValidation>
              </v-col>
              <v-col cols="12" md="6">
                <v-country-states-selector-with-validation
                  clearable
                  v-model="w8Form.bankState"
                  rules="required"
                  id="state"
                  name="state"
                  attach
                  placeholder="bankState"
                  :showOtherCountryStates="true"
                  :currentOtherStateId="w8Form.bankState"
                  @other-state-selected="currentBankState"
                  :countryId="w8Form.bankCountry ? w8Form.bankCountry : 0"
                  autocomplete="new-state"
                  label="bankState"
                  :disabled="!w8Form.bankCountry"
                  @change="saveIfValid"
                />
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  clearable
                  v-model.trim="w8Form.bankPostalCode"
                  placeholder="bankPostalCode"
                  name="bankPostalCode"
                  autocomplete="bank-postal-code"
                  label="bankPostalCode"
                  rules=""
                  :maxLength="10"
                  @blur="saveIfValid"
                >
                </VTextFieldWithValidation>
              </v-col>
              <v-col cols="12" md="6">
                <VTextFieldWithValidation
                  clearable
                  v-model.trim="w8Form.ibanNumber"
                  placeholder="ibanNumber"
                  name="ibanNumber"
                  autocomplete="iban-number"
                  label="ibanNumber"
                  rules="required|min:16"
                  :maxLength="34"
                  @blur="saveIfValid"
                >
                </VTextFieldWithValidation>
              </v-col>
              <v-col cols="12" md="6" v-if="showBankSortField">
                <VTextFieldWithValidation
                  class="sort-code-input"
                  :toggleErrorClass="true"
                  clearable
                  v-model="w8Form.bankSortCode"
                  placeholder="bankSortCode"
                  name="bankSortCode"
                  autocomplete="bank-sort-code"
                  label="bankSortCode"
                  rules="required|min:8"
                  :maxLength="8"
                  @blur="saveIfValid"
                >
                </VTextFieldWithValidation>
                <p class="mb-3 error-value" v-if="showSortCodeError">The bank sort code must be 6 digits</p>
                <p class="mb-0 user-info-content primary--text">{{ $t("fields.bankSortInfo") }}</p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-divider class="my-6"></v-divider>
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.certifyCountry") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VCountrySelectorWithValidation
              v-model="w8Form.certificationCountry"
              rules="required"
              autocomplete="certify-country"
              attach
              @change="saveIfValid"
            >
            </VCountrySelectorWithValidation>
          </v-col>
        </v-row>
        <h3 class="primary--text">{{ $t("taxForm.captions.reviewCertification") }}</h3>
        <p class="black--text font-weight-bold">{{ $t("taxForm.captions.reviewCertificationContent") }}</p>
        <div class="text-center">
          <v-btn color="primary" type="button" :disabled="invalid || !!tinWarning" @click="showReviewSection = true"
            >{{ $t("taxForm.captions.review") }}
          </v-btn>
        </div>
      </v-form>

      <!-- =====================================================
          ===============  REVIEW FORM SECTION  ===============
          ===================================================== -->
      <div v-else>
        <p class="black--text font-weight-bold pt-3">
          Review your W-8 BEN form data and certify to submit to our database
        </p>
        <v-row class="pb-3">
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              {{ $t("taxForm.captions.fullName") }}
              <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ w8Form.legalName }}</p>
          </v-col>
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              Country of Citizenship
              <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ citizenshipCountryName }}</p>
          </v-col>
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              {{ $t("account.titles.address") }}
              <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ w8Form.address }}</p>
            <p class="black--text mb-0">{{ w8Form.city }} {{ w8Form.state }} {{ w8Form.zipCode }}</p>
          </v-col>
          <v-col v-if="w8Form.ssn || w8Form.itin" cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              Taypayer Identification Number
              <v-icon
                @click="
                  showReviewSection = false;
                  editTin();
                "
                color="primary"
                class="cursor-pointer ms-1"
                small
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">
              {{ !showTin ? showMaskValue : showTaxIdentificationNumber }}
              <v-icon v-if="showTaxIdentificationNumber" class="ms-2" small @click="showTin = !showTin"
                >{{ !showTin ? "$eye" : "$eyeSlash" }}
              </v-icon>
            </p>
          </v-col>
          <v-col cols="12" md="6" v-if="w8Form.ftin">
            <p class="font-weight-bold black--text mb-0">
              FTIN Number
              <v-icon
                @click="
                  showReviewSection = false;
                  w8Form.ftin = '';
                "
                color="primary"
                class="cursor-pointer ms-1"
                small
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">
              {{ !ftinCheck ? showFtinMaskValue : w8Form.ftin }}
              <v-icon class="ms-2" small @click="ftinCheck = !ftinCheck"
                >{{ !ftinCheck ? "$eye" : "$eyeSlash" }}
              </v-icon>
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              Date of Birth
              <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ w8Form.dob }}</p>
          </v-col>
          <v-col v-if="!w8Form.doNotHavePaypal" cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              PayPal Email
              <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ !w8Form.doNotHavePaypal ? w8Form.paypalEmail : `I don't have Paypal` }}</p>
          </v-col>

          <template v-if="w8Form.doNotHavePaypal">
            <v-col cols="12" md="6">
              <p class="font-weight-bold black--text mb-0">
                Personal Phone Number
                <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                  >$edit
                </v-icon>
              </p>
              <p class="black--text mb-0">{{ w8Form.personalPhoneNumber }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="font-weight-bold black--text mb-0">
                Bank Name
                <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                  >$edit
                </v-icon>
              </p>
              <p class="black--text mb-0">{{ w8Form.bankName }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="font-weight-bold black--text mb-0">
                {{ $t("account.titles.bankAddress") }}
                <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                  >$edit
                </v-icon>
              </p>
              <p class="black--text mb-0">{{ w8Form.bankAddress1 }}</p>
              <p class="black--text mb-0" v-if="w8Form.bankAddress2">{{ w8Form.bankAddress2 }}</p>
              <p class="black--text mb-0">{{ w8Form.bankCity }} {{ bankStateName }} {{ w8Form.bankPostalCode }}</p>
              <p class="black--text mb-0">{{ bankCountryName }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="font-weight-bold black--text mb-0">
                {{ $t("placeholders.bankswiftCode") }}
                <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                  >$edit
                </v-icon>
              </p>
              <p class="black--text mb-0">{{ w8Form.swiftCode }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="font-weight-bold black--text mb-0">
                {{ $t("placeholders.ibanNumber") }}
                <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                  >$edit
                </v-icon>
              </p>
              <p class="black--text mb-0">{{ w8Form.ibanNumber }}</p>
            </v-col>
            <v-col cols="12" md="6" v-if="w8Form.bankSortCode && showBankSortField">
              <p class="font-weight-bold black--text mb-0">
                {{ $t("placeholders.bankSortCode") }}
                <v-icon @click="showReviewSection = false" color="primary" class="cursor-pointer ms-1" small
                  >$edit
                </v-icon>
              </p>
              <p class="black--text mb-0">{{ w8Form.bankSortCode }}</p>
            </v-col>
          </template>
        </v-row>
        <v-divider></v-divider>
        <p class="black--text font-weight-bold my-3">{{ $t("taxForm.typingName") }}</p>
        <v-list class="ps-0 certification-text-container">
          <v-list-group v-model="openCertificateSection">
            <template v-slot:activator>
              <v-list-item-title class="primary--text font-weight-bold"
                >{{ openCertificateSection ? "Hide Certification Text" : "View Certification Text" }}
              </v-list-item-title>
            </template>
            <v-list-item class="px-0">
              <div v-html="$t('taxForm.w-8.certificateContent')"></div>
            </v-list-item>
          </v-list-group>
        </v-list>
        <v-row>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="certificateName"
              :placeholder="$t('taxForm.captions.enterFullName')"
              :label="$t('taxForm.captions.enterFullName')"
              rules="required"
              :hint="testCertifyName"
              :multiLang="false"
              persistent-hint
              :maxLength="255"
              autocomplete="off"
            />
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end align-center" :class="{ 'justify-center': isMobile }">
            <v-btn
              color="primary"
              type="submit"
              :loading="submitLoading"
              :disabled="!certificateName || invalidNameMatch"
              @click="submitCertificate()"
              >Certify
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <v-overlay absolute :value="overlayLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </validation-observer>
  </div>
</template>
<script lang="ts">
import Vue, { PropType, VueConstructor } from "vue";
import VTextFieldWithValidation from "../VTextFieldWithValidation.vue";
import { ValidationObserver } from "vee-validate";
import ErrorLabel from "@/components/objects/ErrorLabel.vue";
import { CountryRecordItem, UserPartialItem, UserService, UserTaxInformationRecordItem } from "@/service";
import VTaxClassificationSelector from "../VTaxClassificationSelector.vue";
import VCountryStatesSelectorWithValidation from "../VCountryStatesSelectorWithValidation.vue";
import VCountrySelectorWithValidation from "../VCountrySelectorWithValidation.vue";
import BirthDatePicker from "@/components/form/BirthDatePicker.vue";
import VTelephoneNumberWithValidation from "@/components/form/VTelephoneNumberWithValidation.vue";
import VDateSelectorWithValidation from "@/components/form/VDateSelectorWithValidation.vue";
import { DateTime } from "luxon";

import { RootStoritinterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const { mapGetters, mapActions } = createHelpers<RootStoritinterface>();
const getters = mapGetters(["user", "countries"]);
const actions = mapActions(["updateTaxFormStatus", "loadCountries"]);

export default (
  Vue as VueConstructor<
    Vue & {
    saveIfValid: () => Promise<void>;
    tinWarning: string;
    hasTinSection: boolean;
    hasFtinSection: boolean;
    hasTin: boolean;
    tinSectionWarning: string;
    ftinSectionWarning: string;
    hasNotRequiredSelection: boolean;
    tinFieldsDisabled: boolean;
    ftinFieldDisabled: boolean;
    $refs: {
        observer: InstanceType<typeof ValidationObserver>;
        errorLabel: InstanceType<typeof ErrorLabel>;
      };
    }
  >
).extend({
  components: {
    ValidationObserver,
    VTextFieldWithValidation,
    VTaxClassificationSelector,
    VCountryStatesSelectorWithValidation,
    VCountrySelectorWithValidation,
    BirthDatePicker,
    VTelephoneNumberWithValidation,
    VDateSelectorWithValidation,
  },
  data: () => ({
    isInitializing: false,
    country: {} as CountryRecordItem,
    overlayLoading: false,
    submitLoading: false,
    showTin: false,
    ftinCheck: false,
    entityType: "" as UserTaxInformationRecordItem.entityType,
    taxClarification: "" as UserTaxInformationRecordItem.taxClarification,
    showOtherEntity: false,
    showTaxClassfication: false,
    showOwnerShipCheckbox: false,
    countryId: 233, // USA
    certificateName: "",
    showSecurityNumber: false,
    showFtNum: false,
    showIdentificationNumber: false,
    loading: false,
    openCertificateSection: false,
    recordId: undefined as number | undefined,
    showReviewSection: false,
    fetchRunning: false,
    dateTimeStamp: 0,
    bankStateName: "",
    namespacingHint: "",
    editingTin: false,
    hasSavedTin: false,
    notRequiredSelection: "" as "" | "tin" | "ftin",
    w8Form: {
      id: undefined as number | undefined,
      legalName: "",
      formType: UserTaxInformationRecordItem.formType.W_8_BEN_FORM,
      stateId: undefined as number | undefined,
      certificationCountry: undefined as number | undefined,
      countryId: undefined as number | undefined,
      bankCountry: undefined as number | undefined,
      bankState: undefined as number | undefined,
      bankSortCode: "",
      city: "",
      bankCity: "",
      state: "",
      entityTypeOther: "",
      address: "",
      bankAddress1: "",
      bankAddress2: "",
      ftinNotRequired: false,
      tinNotRequired: false,
      ftin: "",
      doNotHavePaypal: false,
      ssn: "",
      itin: "",
      dob: "",
      bankIban: "",
      bankName: "",
      bankPostalCode: "",
      ibanNumber: "",
      personalPhoneNumber: "",
      paypalEmail: "",
      swiftCode: "",
      zipCode: undefined as number | undefined,
    } as UserTaxInformationRecordItem,
  }),
  props: {
    prefilledRes: {
      type: Object as PropType<UserTaxInformationRecordItem>,
      default: () => ({}) as UserTaxInformationRecordItem,
    },
    showW8Form: {
      type: Boolean,
      default: false,
    },
    parentLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...getters,
    userId(): number {
      return this.user?.id || 0;
    },
    showMaskValue(): string {
      if (this.w8Form.ssn) return "***-**-****";
      if (this.w8Form.itin) return "***-**-****";
      return "";
    },
    paypalMailRules(): string {
      return this.w8Form.doNotHavePaypal ? "" : "required|email";
    },
    showFtinMaskValue(): string {
      if (this.w8Form.ftin) {
        return "*".repeat(this.w8Form.ftin.toString().length);
      } else {
        return "";
      }
    },
    showTaxIdentificationNumber(): string {
      if (this.w8Form.ssn) {
        const lastFour = this.w8Form.ssn.toString().replace(/\D/g, "").slice(-4);
        return lastFour ? `***-**-${lastFour}` : "";
      }
      if (this.w8Form.itin) {
        const lastFour = this.w8Form.itin.toString().replace(/\D/g, "").slice(-4);
        return lastFour ? `***-**-${lastFour}` : "";
      }
      return "";
    },
    hasTaxidentificationNumber() {
      return this.w8Form?.ssn?.length || this.w8Form?.itin?.length;
    },
    testCertifyName() {
      if (this.certificateName && this.invalidNameMatch) {
        return this.$t("taxForm.validation.confirmed");
      }
      return "";
    },
    invalidNameMatch() {
      let legalName = this.normalizeName(this.w8Form.legalName);
      let certificateName = this.normalizeName(this.certificateName);
      return legalName != certificateName;
    },
    isMobile(): boolean {
      return this.$vuetify.breakpoint.smAndDown;
    },
    taxFormNotStarted(): boolean {
      return (
        this.user?.taxFormStatus == UserPartialItem.taxFormStatus.NOT_STARTED ||
        this.user?.taxFormStatus == UserPartialItem.taxFormStatus.IN_PROGRESS
      );
    },
    citizenshipCountryName(): string {
      const match = this.countries?.find((c) => c.id === this.w8Form.countryId);
      return match?.countryName || "";
    },
    bankCountryName(): string {
      const match = this.countries?.find((c) => c.id === this.w8Form.bankCountry);
      return match?.countryName || "";
    },
    shallowRelativityEls(): boolean {
      return this.w8Form.ftin || this.w8Form.ftinNotRequired || this.w8Form.paypalEmail || this.w8Form.doNotHavePaypal
        ? true
        : false;
    },
    showBankSortField(): boolean {
      return this.w8Form.bankCountry ? [14, 39, 105, 232].includes(this.w8Form.bankCountry) : false;
    },
    showSortCodeError(): boolean {
      const code = this.w8Form.bankSortCode || "";
      return code.length > 0 && code.length < 8;
    },
    ssnRules(): string {
      if (this.w8Form.ssn) return "ssnFormat";
      return this.w8Form.itin ? "" : "required";
    },
    itinRules(): string {
      if (this.w8Form.itin) return "itinFormat";
      return this.w8Form.ssn ? "" : "required";
    },
    maskedSsn(): string {
      const lastFour = (this.w8Form.ssn || "").replace(/\D/g, "").slice(-4);
      return lastFour ? `***-**-${lastFour}` : "";
    },
    maskedItin(): string {
      const lastFour = (this.w8Form.itin || "").replace(/\D/g, "").slice(-4);
      return lastFour ? `***-**-${lastFour}` : "";
    },
    hasTinSection(): boolean {
      return !!(this.w8Form.ssn || this.w8Form.itin);
    },
    hasFtinSection(): boolean {
      return !!this.w8Form.ftin;
    },
    hasTin(): boolean {
      return this.hasTinSection || this.hasFtinSection;
    },
    tinSectionWarning(): string {
      if (this.w8Form.ftinNotRequired && !this.hasTinSection) {
        return this.$t("customValidations.messages.tinWarningW8") as string;
      }
      if (!this.w8Form.tinNotRequired && !this.hasTinSection && !this.hasFtinSection) {
        return this.$t("customValidations.messages.tinOrFtinRequired") as string;
      }
      return "";
    },
    ftinSectionWarning(): string {
      if (this.w8Form.tinNotRequired && !this.hasFtinSection) {
        return this.$t("customValidations.messages.ftinRequired") as string;
      }
      return "";
    },
    tinWarning(): string {
      return this.tinSectionWarning || this.ftinSectionWarning;
    },
    ftinRules(): string {
      return this.w8Form.ftinNotRequired ? "" : "";
    },
    hasNotRequiredSelection(): boolean {
      return !!this.notRequiredSelection;
    },
    tinFieldsDisabled(): boolean {
      return !this.hasNotRequiredSelection || !!this.w8Form.tinNotRequired;
    },
    ftinFieldDisabled(): boolean {
      return !this.hasNotRequiredSelection || !!this.w8Form.ftinNotRequired;
    },
  },
  methods: {
    ...actions,
    parseToTimestamp(dateString: string): number | null {
      if (!dateString) return null;
      const date = DateTime.fromFormat(dateString, "yyyy-MM-dd", { zone: "local" });
      return date.isValid ? date.toSeconds() : 0;
    },
    formatDateTimeRequirement(timestamp: number) {
      let d = DateTime.fromSeconds(timestamp);
      return d.toFormat("yyyy-MM-dd");
    },
    async loadcountryData() {
      if (!this.countries) {
        try {
          await this.loadCountries({});
        } catch (e) {
          console.log(e);
        }
      }
    },
    getFilteredPayload() {
      let sanitizePayload = JSON.parse(JSON.stringify(this.w8Form)); //individual object to not trigger API call twice on field modification
      sanitizePayload.legalName = this.normalizeName(sanitizePayload.legalName, true);

      const excludeKeys = ["address", "zipCode", "city", "state", "stateId"];
      return Object.fromEntries(
        Object.entries(sanitizePayload).filter(([key, value]) => {
          // remove certify date on edit
          if (key == "certificationDate") return false;

          // Keep excluded keys without filtering
          if (excludeKeys.includes(key)) return true;
          return value !== undefined;
        }),
      );
    },
    async updateFormData(response: UserTaxInformationRecordItem) {
      this.isInitializing = true;
      // load countries before binding the values.
      await this.loadcountryData();

      this.w8Form.id = response?.id ? response.id : undefined;
      this.w8Form.legalName = response?.legalName ? response.legalName : undefined;
      this.w8Form.stateId = response?.stateId ? response.stateId : undefined;
      this.w8Form.certificationCountry = response?.certificationCountry ? response.certificationCountry : undefined;
      this.w8Form.city = response?.city ? response.city : "";
      this.w8Form.countryId = response?.countryId ? response.countryId : undefined;
      this.w8Form.bankCountry = response?.bankCountry ? response.bankCountry : undefined;
      this.w8Form.bankState = response?.bankState ? response.bankState : undefined;
      this.w8Form.address = response?.address ? response.address : "";
      this.w8Form.bankAddress1 = response?.bankAddress1 ? response.bankAddress1 : "";
      this.w8Form.bankAddress2 = response?.bankAddress2 ? response.bankAddress2 : "";
      this.w8Form.bankCity = response?.bankCity ? response.bankCity : "";
      this.w8Form.state = response?.state ? response.state : "";
      this.w8Form.zipCode = response?.zipCode ? response.zipCode : "";
      this.w8Form.bankPostalCode = response?.bankPostalCode ? response.bankPostalCode : "";
      this.w8Form.bankSortCode = response?.bankSortCode ? response.bankSortCode : "";
      this.w8Form.ibanNumber = response?.ibanNumber ? response.ibanNumber : "";
      this.w8Form.ftinNotRequired = response?.ftinNotRequired ? true : false;
      this.w8Form.tinNotRequired = response?.tinNotRequired ? true : false;
      this.w8Form.paypalEmail = response?.paypalEmail ? response.paypalEmail : "";
      this.w8Form.doNotHavePaypal = response?.doNotHavePaypal ? true : false;
      this.w8Form.swiftCode = response?.swiftCode ? response.swiftCode : undefined;
      this.w8Form.bankName = response?.bankName ? response.bankName : undefined;
      this.w8Form.personalPhoneNumber = response?.personalPhoneNumber ? response.personalPhoneNumber : undefined;
      this.w8Form.ssn = response?.ssn ? response.ssn : "";
      this.w8Form.itin = response?.itin ? response.itin : "";
      this.w8Form.ftin = response?.ftin ? response.ftin : undefined;
      this.hasSavedTin = !!(response?.ssn || response?.itin);
      this.editingTin = false;
      this.w8Form.dob = response?.dob ? response.dob : undefined;
      this.dateTimeStamp = +this.parseToTimestamp(this.w8Form.dob);
      this.notRequiredSelection = this.w8Form.tinNotRequired
        ? "tin"
        : this.w8Form.ftinNotRequired
          ? "ftin"
          : "";
      this.$nextTick(() => {
        this.isInitializing = false;
        if (this.w8Form.id && this.shallowRelativityEls) this.$refs.observer.validate();
      });
    },
    async createFormData() {
      this.loading = true;
      this.fetchRunning = true;
      const payload = this.getFilteredPayload();
      const id = this.userId;
      try {
        const res = await UserService.createTaxInformation({
          id: id as number,
          requestBody: payload,
        });
        if (res) this.w8Form.id = res.id;
      } catch (e) {
        console.log("error", e);
      } finally {
        this.fetchRunning = false;
        this.loading = false;
      }
    },
    async updateTaxForm() {
      this.loading = true;
      this.fetchRunning = true;
      const payload = this.getFilteredPayload();
      const id = this.userId as number;
      try {
        await UserService.updateTaxInformation({
          id,
          requestBody: payload,
        });
      } catch (e) {
        console.log("error", e);
      } finally {
        this.fetchRunning = false;
        this.loading = false;
      }
    },
    async saveFormData() {
      if (this.isInitializing) return;
      if (this.fetchRunning) return;
      if (this.parentLoading) return;

      if (!this.w8Form.id) {
        await this.createFormData();
      } else {
        await this.updateTaxForm();
      }
    },
    async submitCertificate() {
      if (this.tinWarning) return;
      this.submitLoading = true;
      if (!this.w8Form.certificationDate)
        this.w8Form.certificationDate = this.formatDateTimeRequirement(Math.floor(Date.now() / 1000));
      const payload = this.w8Form;
      const id = this.userId as number;
      try {
        await UserService.updateTaxInformation({ id: id, requestBody: payload });
        const userRes = await UserService.getUserById({ id: id });

        // update tax form status in global storage: local storage and FE user partial item
        if (this.taxFormNotStarted) this.updateTaxFormStatus(userRes.taxFormStatus);

        this.$emit("submit-modal");
        this.$confirm(
          `Your W-8 BEN Form has been successfully submitted. Your information has been stored and your next payment will be issued per our standard payment processing timelines.`,
          {
            icon: "",
            title: `W-8 BEN Form Submitted`,
            buttonTrueText: "",
            buttonFalseText: "",
          },
        );
      } catch (e) {
        console.log("error", e);
      }
      this.submitLoading = false;
    },
    currentState(val: any) {
      if (!val) this.w8Form.state = "";
      this.w8Form.state = val.text ?? "";
    },
    currentBankState(val: any) {
      if (!val) this.bankStateName = "";
      this.bankStateName = val.text ?? "";
    },
    removeReadOnly(event: any) {
      event.target.removeAttribute("readonly");
    },
    normalizeName(str: string, keepOriginalLetters?: boolean) {
      let sanitizeNameSpace = str?.trim().replace(/\s+/g, " ");
      return !keepOriginalLetters ? sanitizeNameSpace.toLowerCase() : sanitizeNameSpace;
    },
    hasSpacingIrregularities() {
      let name = this.w8Form.legalName ?? "";
      const spacingRegex = /^\s|\s{2,}|\s$/;
      this.namespacingHint = spacingRegex.test(name) ? (this.$t("hints.unnecessarySpaces") as string) : "";
    },
    formatSsn() {
      this.$nextTick(() => {
        const digits = (this.w8Form.ssn || "").replace(/\D/g, "").slice(0, 9);
        this.w8Form.ssn =
          digits.length <= 3
            ? digits
            : digits.length <= 5
              ? `${digits.slice(0, 3)}-${digits.slice(3)}`
              : `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      });
    },
    //Format itin to XX-XXXXXXX
    formatItin() {
      this.$nextTick(() => {
        const digits = (this.w8Form.itin || "").replace(/\D/g, "").slice(0, 9);
        this.w8Form.itin =
          digits.length <= 3
            ? digits
            : digits.length <= 5
              ? `${digits.slice(0, 3)}-${digits.slice(3)}`
              : `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      });
    },
    editTin() {
      this.editingTin = true;
      this.hasSavedTin = false;
      this.showSecurityNumber = false;
      this.showIdentificationNumber = false;
      this.showTin = false;
      this.w8Form.ssn = "";
      this.w8Form.itin = "";
    },
    async saveIfValid() {
      if (this.isInitializing || this.fetchRunning || this.parentLoading) return;
      await this.saveFormData();
    },
    async handleNotRequired(newVal: "" | "tin" | "ftin") {
      this.w8Form.tinNotRequired = newVal === "tin";
      this.w8Form.ftinNotRequired = newVal === "ftin";
      if (newVal === "tin") {
        this.w8Form.ssn = "";
        this.w8Form.itin = "";
      }
      if (newVal === "ftin") {
        this.w8Form.ftin = "";
      }
      await this.$nextTick();
      await this.saveIfValid();
    },
  },
  watch: {
    "w8Form.entityType"(newVal) {
      this.showOtherEntity = newVal === UserTaxInformationRecordItem.entityType.OTHER;
      this.showTaxClassfication = newVal === UserTaxInformationRecordItem.entityType.LLC;
      this.showOwnerShipCheckbox = [
        UserTaxInformationRecordItem.entityType.TRUST_ESTATE,
        UserTaxInformationRecordItem.entityType.PARTNERSHIP,
      ].includes(newVal);
    },
    "w8Form.taxClarification"(newVal) {
      this.showOwnerShipCheckbox = newVal === UserTaxInformationRecordItem.taxClarification.PARTNERSHIP;
    },
    "w8Form.doNotHavePaypal"(newVal: boolean) {
      if (newVal) {
        this.w8Form.paypalEmail = ""; // Clear the email when checkbox is checked
      }
    },
    "w8Form.bankSortCode"(newVal) {
      let realSortCode = newVal?.replace(/[^0-9]/g, "");
      let dashedNumber = realSortCode?.match(/.{1,2}/g);
      this.w8Form.bankSortCode = dashedNumber?.join("-");
    },
    dateTimeStamp(newVal) {
      if (newVal) this.w8Form.dob = this.formatDateTimeRequirement(newVal);
    },
    showW8Form: {
      handler(newVal) {
        if (newVal) this.updateFormData(this.prefilledRes);
      },
    },
    prefilledRes(newVal) {
      if (newVal) {
        this.updateFormData(newVal);
      }
    },
  },
  mounted() {
    this.updateFormData(this.prefilledRes);
  },
});
</script>
<style lang="scss" scoped>
:deep(.v-list),
.v-list {
  background-color: white !important;
}

:deep(.v-list-item) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/*:deep(.v-input__control .v-messages__message) {
    color: #f44336 !important;
}*/

.sort-code-input {
  :deep(.v-text-field__details) {
    display: none;
  }
}

.parent-wrapper {
  position: relative;
}

.user-info-content {
  font-size: 12px;
  line-height: 14px;
}

.error-value {
  font-size: 12px;
  line-height: 12px;
  color: #ff5252;
}

.certification-text-container :deep(.v-list-item) {
  padding: 0;
}
.tin-tooltip {
  margin-top: -30px;
}
</style>
